// ── Main RAF Loop ─────────────────────────────────────────────────────────
// Contains update() and helpers, extracted from index.html (Task 29).

import { state, filters } from '../state';
import type { Item } from '../types';
import { dom } from '../dom';
import {
  CX, CY,
  FEEDBACK_BASE_SCALE, FEEDBACK_SPRING_STIFF, FEEDBACK_SPRING_DAMP,
  PERSISTENT_CLOCK_ENABLED, MINI_CLOCK_SCALE,
  LOCK_WORLD_ZOOM, MENU_SCALE_FINAL,
  CAMERA_SMOOTHING, CAMERA_MAX_PAN_BASE, CAMERA_MAX_PAN_LERP,
  ORGANISM_PAIR_GAP,
  HIGHLIGHT_SIZE,
  SELECTOR_HYSTERESIS_PX, SELECTOR_RING_SWITCH_BIAS_PX, SELECTOR_MIDDLE_RING_BONUS,
  SELECTOR_MAX_DIST_MIN_PX, SELECTOR_MAX_DIST_PADDING_PX, SELECTOR_RELEASE_BONUS_PX,
  ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX,
  RINGS,
} from '../config';
import { lerp, clamp, easeOutCubic, easeOutQuint } from '../utils/math';
import {
  getUnlockProgress,
  getLockingProgress,
  getIconRevealProgress,
  applyLockItemReveal,
  drawLockLinesOverlay,
  drawLines,
  clearLinesCanvas,
  clearLockLinesOverlay,
  syncLockLineColliders,
  applyHomeScannerDimming,
} from '../render/lineRenderer';
import { setState } from './appStateMachine';
import {
  updateInstallerFlow,
  updateInstallerScannerMorph,
  getCameraTargetForItem,
  getDynamicCameraPanLimit,
  getInstallerScannerTargetScale,
  getInstallerScannerObstacleRadius,
  getInstallerScannerAnchor,
  applyInstallerScannerVisual,
} from '../lock/unlockTransition';
import { stepPressFeedback } from '../items/pressFeedback';
import { getIsLaunching } from '../selection/launch';
import {
  getOrganismItemRadius,
  getItemSettledScale,
  getItemRenderScale,
  applyItemTransform,
  getHomeLayoutConfig,
  LINE_CENTER_RADIUS,
  isInstallerFlowActive,
  isScannerCameraLockActive,
  updateSettledScaleCache,
} from '../layout/ringLayout';
import { setHighlight } from '../items/highlight';
import {
  scrambleToText,
  getHomeIdleReticleText,
} from '../render/matrixScramble';
import { hasHaptics, loop } from '../loopSdk';
import { getHomePosLerp } from '../utils/reducedMotion';
import { tubeLayoutEngine } from '../layout/tubeLayoutEngine';
import type { LayoutState, LayoutFrameCtx } from '../layout/tubeLayoutEngine';
import { homeRuntime } from './homeLoop';
import type { HomeInteractionState, HomeInputCtx } from './homeLoop';
import {
  stepHomeUI,
  getHomeUIWorldZoomTarget,
  getHomeUICameraOverride,
  isHomeUIBlockingSelector,
  isHomeOptionsFlowActive,
  getHomeOptionsSnapshotScale,
  applyHomeOptionsSnapshotPoses,
  isHomeEditMode,
  isHomeEditDragging,
  applyHomeEditDragAfterLayout,
  applyHomeUIItemOpacity,
} from '../home/homeOptions';
import { markUpdateStart, markUpdateEnd } from './devGuards';

const FEEDBACK_HOLD_MS = 600;

function stepTubeLayout(layoutState: LayoutState, frameCtx: LayoutFrameCtx) {
  tubeLayoutEngine.stepTubeLayout(layoutState, frameCtx);
}

function computeHomeInteraction(homeState: HomeInteractionState, inputCtx: HomeInputCtx) {
  return homeRuntime.computeInteraction(homeState, inputCtx);
}

function stepInstallerFlowContract(now: number) {
  return homeRuntime.stepInstallerFlow({
    updateInstallerFlow,
    updateInstallerScannerMorph,
    installerItem: state.installerItem,
    installConveyor: state.installConveyor,
    installerFlow: state.installerFlow,
    pairGap: ORGANISM_PAIR_GAP,
    includeConveyorObstacles: false,
    getInstallerScannerObstacleRadius,
    getOrganismItemRadius,
    isInstallerFlowActive,
  }, now);
}

export function assertRuntimeModules(): void {
  // tubeLayoutEngine and homeRuntime are now ES module imports — always present.
  if (typeof tubeLayoutEngine.stepTubeLayout !== 'function') {
    throw new Error('[Loop OS] tubeLayoutEngine.stepTubeLayout is not a function');
  }
  if (typeof homeRuntime.computeInteraction !== 'function' || typeof homeRuntime.stepInstallerFlow !== 'function') {
    throw new Error('[Loop OS] homeRuntime is missing required methods');
  }
}

function update(now: number): void {
  markUpdateStart();
  try {
    updateBody(now);
  } finally {
    markUpdateEnd();
  }
  requestAnimationFrame(update);
}

function updateBody(now: number): void {
  const unlockProgress = getUnlockProgress(now);
  const pullbackProgress = easeOutCubic(unlockProgress);
  const zoomPullback = easeOutQuint(unlockProgress);
  const installerFrame = stepInstallerFlowContract(now);
  if (state.appState === 'home') stepHomeUI(now);
  const homeUIZoomTarget = state.appState === 'home'
    ? getHomeUIWorldZoomTarget()
    : null;

  // ── Lock Screen Feedback Spring ───────────────────────────────────────
  if (state.appState === 'lock' || state.appState === 'unlocking' || state.appState === 'locking') {
    if (state.appState === 'lock' && now - state.feedbackLastPressAt > FEEDBACK_HOLD_MS) {
      state.feedbackScaleTarget = lerp(state.feedbackScaleTarget, FEEDBACK_BASE_SCALE, 0.06);
      if (Math.abs(state.feedbackScaleTarget - FEEDBACK_BASE_SCALE) < 0.005)
        state.feedbackScaleTarget = FEEDBACK_BASE_SCALE;
    }
    const displacement = state.feedbackScaleTarget - state.feedbackScaleCurrent;
    state.feedbackVelocity += displacement * FEEDBACK_SPRING_STIFF * (1/60);
    state.feedbackVelocity *= Math.pow(FEEDBACK_SPRING_DAMP, 1);
    state.feedbackScaleCurrent += state.feedbackVelocity * (1/60);
    if (Math.abs(displacement) < 0.001 && Math.abs(state.feedbackVelocity) < 0.001) {
      state.feedbackScaleCurrent = state.feedbackScaleTarget;
      state.feedbackVelocity = 0;
    }
    state.parallaxX = lerp(state.parallaxX, state.parallaxTargetX, 0.12);
    state.parallaxY = lerp(state.parallaxY, state.parallaxTargetY, 0.12);
    if (state.watchfaceHost) {
      const wfh = state.watchfaceHost;
      if (PERSISTENT_CLOCK_ENABLED && state.appState === 'unlocking') {
        const pFade = 1 - pullbackProgress;
        wfh.setParallax(state.parallaxX * pFade, state.parallaxY * pFade, 'gyro');
      } else {
        wfh.setParallax(state.parallaxX, state.parallaxY, 'gyro');
      }
      wfh.setZoomProgress(1 - pullbackProgress);
    }

    // Apply feedback scale to lock screen (shrink effect). Always include
    // translate3d(0,0,0) so the GPU compositor layer stays promoted across
    // every state — keeps the transform string format identical to the
    // home-state writer, preventing a one-frame layer demotion at state
    // transitions which manifests as a watchface-hand flicker.
    if (state.appState === 'lock') {
      dom.lockScreen.style.transform = `translate3d(0px, 0px, 0) scale(${state.feedbackScaleCurrent})`;
      dom.lockScreen.style.opacity = '1';
    }
  }

  // ── Unlocking Phase ───────────────────────────────────────────────────
  if (state.appState === 'unlocking') {
    if (PERSISTENT_CLOCK_ENABLED) {
      // Clock shrinks to mini scale, stays visible. translate3d kept in
      // the transform string for GPU layer stability across states.
      const clockScale = state.feedbackScaleCurrent * lerp(1, MINI_CLOCK_SCALE, zoomPullback);
      state.persistentClockScale = clockScale;
      dom.lockScreen.style.transform = `translate3d(0px, 0px, 0) scale(${clockScale})`;
      dom.lockScreen.style.opacity = '1';
      if (state.watchfaceHost) state.watchfaceHost.setMinimalMode(pullbackProgress);
    } else {
      // Clock shrinks to 0 and fades out
      const clockScale = state.feedbackScaleCurrent * (1 - zoomPullback);
      const fadeStart = 0.7;
      const clockOpacity = unlockProgress < fadeStart ? 1 : clamp(1 - (unlockProgress - fadeStart) / (1 - fadeStart), 0, 1);
      dom.lockScreen.style.transform = `translate3d(0px, 0px, 0) scale(${clockScale})`;
      dom.lockScreen.style.opacity = String(clockOpacity);
    }
    if (unlockProgress >= 1) {
      setState('home');
    }
  }

  if (state.appState === 'lock' || state.appState === 'unlocking') {
    // Continuous pullback: world geometry is stable while camera pulls back.
    state.worldZoom = LOCK_WORLD_ZOOM + (1 - LOCK_WORLD_ZOOM) * zoomPullback;
    state.menuScale = MENU_SCALE_FINAL;

    const iconReveal = getIconRevealProgress(pullbackProgress);
    applyLockItemReveal(iconReveal);
    drawLockLinesOverlay(pullbackProgress);
  } else if (state.appState === 'home') {
    const zoomTarget = homeUIZoomTarget ?? 1;
    state.worldZoom = lerp(state.worldZoom, zoomTarget, 0.15);
    if (Math.abs(state.worldZoom - zoomTarget) < 0.002) state.worldZoom = zoomTarget;
    state.menuScale = lerp(state.menuScale, MENU_SCALE_FINAL, 0.15);
    if (Math.abs(state.menuScale - MENU_SCALE_FINAL) < 0.002) state.menuScale = MENU_SCALE_FINAL;
  }

  // ── Locking Phase (reverse of unlocking) ──────────────────────────────
  if (state.appState === 'locking') {
    const lockProgress = getLockingProgress(now);
    const reversePullback = 1 - easeOutQuint(lockProgress);

    if (state.watchfaceHost) {
      state.watchfaceHost.setMinimalMode(1 - lockProgress);
      state.watchfaceHost.setZoomProgress(1 - reversePullback);
    }

    const clockScale = lerp(state.persistentClockScale, 1, easeOutQuint(lockProgress));
    // translate3d kept in the transform string so the GPU compositor
    // layer doesn't demote/re-promote on the locking → lock transition.
    dom.lockScreen.style.transform = `translate3d(0px, 0px, 0) scale(${clockScale})`;
    dom.lockScreen.style.opacity = '1';

    state.worldZoom = 1 + (LOCK_WORLD_ZOOM - 1) * easeOutQuint(lockProgress);
    state.menuScale = MENU_SCALE_FINAL;

    applyLockItemReveal(getIconRevealProgress(reversePullback));
    drawLockLinesOverlay(reversePullback);

    const camEase = easeOutCubic(lockProgress);
    state.cameraX = lerp(state.lockingStartCameraX, 0, camEase);
    state.cameraY = lerp(state.lockingStartCameraY, 0, camEase);
    state.cameraTargetX = state.cameraX;
    state.cameraTargetY = state.cameraY;

    dom.world.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.worldZoom})`;
    dom.menuContainer.style.transform = `scale(${state.menuScale})`;
    if (dom.lockLinesOverlay) {
      const s = state.worldZoom * state.menuScale;
      dom.lockLinesOverlay.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${s})`;
    }

    syncLockLineColliders(1);

    if (lockProgress >= 1) {
      state.cameraX = 0; state.cameraY = 0;
      state.cameraTargetX = 0; state.cameraTargetY = 0;
      state.cameraNormX = 0; state.cameraNormY = 0;
      state.feedbackScaleCurrent = FEEDBACK_BASE_SCALE;
      state.feedbackScaleTarget = FEEDBACK_BASE_SCALE;
      state.feedbackVelocity = 0;
      state.unlockKeyTimes = [];
      state.refQuat = null;
      state.homeRefQuat = null;
      filters.inputX.reset(); filters.inputY.reset();
      filters.cameraX.reset(); filters.cameraY.reset();
      clearLinesCanvas();
      setState('lock');
    }
  }

  // ── Camera (skip during launch or locking) ────────────────────────────
  if (!getIsLaunching() && state.appState !== 'locking') {
    const homeUICameraOverride = state.appState === 'home'
      ? getHomeUICameraOverride()
      : null;
    const scannerCameraLock = isScannerCameraLockActive();
    if (state.appState === 'home') {
      const dynamicPan = getDynamicCameraPanLimit();
      state.cameraMaxPanCurrent = lerp(state.cameraMaxPanCurrent, dynamicPan, CAMERA_MAX_PAN_LERP);
      if (Math.abs(state.cameraMaxPanCurrent - dynamicPan) < 0.1) state.cameraMaxPanCurrent = dynamicPan;
    } else {
      state.cameraMaxPanCurrent = CAMERA_MAX_PAN_BASE;
    }

    if (state.appState === 'home' && homeUICameraOverride) {
      state.cameraTargetX = homeUICameraOverride.x;
      state.cameraTargetY = homeUICameraOverride.y;
    } else if (state.appState === 'home' && scannerCameraLock) {
      const camTarget = getCameraTargetForItem(state.installerItem!);
      state.cameraTargetX = camTarget.x;
      state.cameraTargetY = camTarget.y;
    } else if (state.appState === 'home' && state.inputActive) {
      state.cameraTargetX = clamp(state.cameraNormX * state.cameraMaxPanCurrent, -state.cameraMaxPanCurrent, state.cameraMaxPanCurrent);
      state.cameraTargetY = clamp(state.cameraNormY * state.cameraMaxPanCurrent, -state.cameraMaxPanCurrent, state.cameraMaxPanCurrent);
    }
    const cameraSmoothing = homeUICameraOverride
      ? homeUICameraOverride.smoothing
      : (scannerCameraLock ? Math.max(CAMERA_SMOOTHING, 0.34) : CAMERA_SMOOTHING);
    state.cameraX = lerp(state.cameraX, state.cameraTargetX, cameraSmoothing);
    state.cameraY = lerp(state.cameraY, state.cameraTargetY, cameraSmoothing);
    if (Math.abs(state.cameraX - state.cameraTargetX) < 0.1) state.cameraX = state.cameraTargetX;
    if (Math.abs(state.cameraY - state.cameraTargetY) < 0.1) state.cameraY = state.cameraTargetY;
    dom.world.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${state.worldZoom})`;
    if (PERSISTENT_CLOCK_ENABLED && state.appState === 'home') {
      // Hide the persistent clock as the options menu zooms in (zoomBlend
      // 0→1 over options enter, 1→0 over exit). Multiplying by 1-zoomBlend
      // collapses the clock to scale 0 in options menu and grows it back
      // when leaving — matches the bloop hide/reveal of the other items.
      // Press feedback (clockPressScale, ~0.6 while held, rebound on
      // release) is folded in here so pressing T/B over the center clock
      // visibly scales it down before re-locking — same primitive as item
      // press feedback.
      const optionsHide = clamp(state.homeUI.zoomBlend, 0, 1);
      const clockPress = Number.isFinite(state.clockPressScale) ? state.clockPressScale : 1;
      const clockScale = state.persistentClockScale * (1 - optionsHide) * clockPress;
      dom.lockScreen.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${clockScale})`;
      dom.relockCapture.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0)`;
    }
    dom.menuContainer.style.transform = `scale(${state.menuScale})`;
    if (dom.lockLinesOverlay) {
      const lockOverlayScale = state.worldZoom * state.menuScale;
      dom.lockLinesOverlay.style.transform = `translate3d(${-state.cameraX}px, ${-state.cameraY}px, 0) scale(${lockOverlayScale})`;
    }
  }

  syncLockLineColliders(state.feedbackScaleCurrent);

  if (state.appState === 'home') {
    stepPressFeedback(now);
  }

  // ── Home Interactive Phase ────────────────────────────────────────────
  if (state.appState === 'home' && !getIsLaunching()) {
    if (state.installerItem && state.installerItem.scannerActive && !state.scannerMorphAnim) {
      state.installerItem.scannerScale = lerp(
        1,
        getInstallerScannerTargetScale(state.installerItem),
        clamp(state.installerItem.scannerOpenProgress || 0, 0, 1)
      );
      state.installerItem.scannerVideoAlpha = state.installerItem.scannerVideoLive ? 1 : 0;
      applyInstallerScannerVisual(state.installerItem);
    }

    const editMode = isHomeEditMode();
    const shouldComputeInteraction = !isHomeUIBlockingSelector();
    let interaction = null;
    if (shouldComputeInteraction) {
      interaction = computeHomeInteraction({
        items: state.items,
        highlighted: state.highlighted,
        cx: CX,
        cy: CY,
        cameraX: state.cameraX,
        cameraY: state.cameraY,
        menuScale: state.menuScale,
        worldZoom: state.worldZoom,
        lineCenterRadius: LINE_CENTER_RADIUS,
        setHighlight,
        scrambleToText: editMode ? (() => {}) : scrambleToText,
        getHomeIdleReticleText,
        hasHaptics: editMode ? false : hasHaptics,
        pulseHaptics: (!editMode && hasHaptics) ? (v: number) => loop!.haptics!.pulse(v) : null,
        highlightSize: HIGHLIGHT_SIZE,
        selectorConfig: {
          hysteresisPx: SELECTOR_HYSTERESIS_PX,
          ringSwitchBiasPx: SELECTOR_RING_SWITCH_BIAS_PX,
          middleRingBonus: SELECTOR_MIDDLE_RING_BONUS,
          maxDistMinPx: SELECTOR_MAX_DIST_MIN_PX,
          maxDistPaddingPx: SELECTOR_MAX_DIST_PADDING_PX,
          releaseBonusPx: SELECTOR_RELEASE_BONUS_PX,
          middleRingIndex: Math.max(0, RINGS.length - 2),
        },
      }, {
        inputActive: state.inputActive,
      });
      if (interaction && interaction.highlighted !== undefined) {
        state.highlighted = interaction.highlighted;
      }
    } else if (state.homeUI.targetItem) {
      if (state.highlighted && state.highlighted !== state.homeUI.targetItem) {
        setHighlight(state.highlighted, false);
      }
      state.highlighted = state.homeUI.targetItem;
      if (isHomeOptionsFlowActive()) {
        setHighlight(state.homeUI.targetItem, false);
      } else {
        setHighlight(state.homeUI.targetItem, true);
      }
    }

    const focusItem = state.homeUI.targetItem ?? state.highlighted;
    const focusX = interaction ? interaction.focusX : (focusItem ? focusItem.baseX : CX);
    const focusY = interaction ? interaction.focusY : (focusItem ? focusItem.baseY : CY);
    const pinnedItems = new Set<Item>();
    if (state.installerItem && state.installerItem.scannerActive) pinnedItems.add(state.installerItem);
    if (state.installConveyor && state.installConveyor.tempItem) pinnedItems.add(state.installConveyor.tempItem);
    if (state.installConveyor && state.installConveyor.crowd) {
      for (const crowdItem of state.installConveyor.crowd.items) pinnedItems.add(crowdItem);
    }
    if (isHomeEditDragging() && state.homeUI.dragItem) pinnedItems.add(state.homeUI.dragItem);

    for (const item of state.items) {
      item.targetX = item.baseX;
      item.targetY = item.baseY;
      const optionsSnapshotScale = getHomeOptionsSnapshotScale(item);
      if (optionsSnapshotScale != null) {
        item.organismDesiredScale = clamp(optionsSnapshotScale, ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX);
        continue;
      }
      const highlightScale = (HIGHLIGHT_SIZE / item.size) * 1.4;
      const baseScale = item === state.highlighted ? highlightScale : getItemSettledScale(item);
      let targetScale = baseScale;

      if (pinnedItems.has(item) && item.installState === 'inserting') {
        targetScale = Math.max(item.currentScale || 0.08, 0.08);
      } else {
        targetScale *= 1 + item.organismScaleBoost;
        // pressScaleMul intentionally NOT applied here: it's a pure render
        // multiplier (see getItemRenderScale). Routing it through the
        // solver caused the scale to be stuck at the depressed value when
        // launching gated this block; rebound was invisible.
      }
      item.organismDesiredScale = clamp(targetScale, ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX);
    }

    stepTubeLayout({
      items: state.items,
      highlighted: state.highlighted,
      cx: CX,
      cy: CY,
      centerGuardRadius: LINE_CENTER_RADIUS + 4,
      applyItemTransform,
      getRenderScale: getItemRenderScale,
      config: getHomeLayoutConfig(),
    }, {
      focusX,
      focusY,
      posLerp: getHomePosLerp(!!state.highlighted),
      obstacles: installerFrame.obstacles || [],
      pinnedItems,
    });
    applyHomeEditDragAfterLayout();
    applyHomeOptionsSnapshotPoses(applyItemTransform);

    if (installerFrame.spacingObstacle && Number.isFinite(installerFrame.spacingObstacle.gap)) {
      state.installerFlow!.scannerObstacleGap = installerFrame.spacingObstacle.gap;
    }

    if (state.installerItem && state.installerItem.scannerActive) {
      const ii = state.installerItem;
      // Pin to the shifted scanner anchor (lerped by scannerOpenProgress so
      // the morph in/out is smooth). Mirrors hover-grow's clock-guard pushout.
      const anchor = getInstallerScannerAnchor(ii);
      const t = clamp(ii.scannerOpenProgress || 0, 0, 1);
      const px = lerp(ii.baseX, anchor.x, t);
      const py = lerp(ii.baseY, anchor.y, t);
      ii.currentX = px;
      ii.currentY = py;
      ii.flowX    = px;
      ii.flowY    = py;
      ii.targetX  = px;
      ii.targetY  = py;
      applyItemTransform(ii);
    }

    updateSettledScaleCache(state.items);
    applyHomeScannerDimming();
    applyHomeUIItemOpacity();
    drawLines();
    if (state.lockOverlayPendingHide && dom.lockLinesOverlay) {
      dom.lockLinesOverlay.style.opacity = '0';
      dom.lockLinesOverlay.style.display = 'none';
      clearLockLinesOverlay();
      state.lockOverlayPendingHide = false;
    }
  }
}

export function startMainLoop(): void {
  assertRuntimeModules();
  requestAnimationFrame(update);
}
